import { createContext, useContext } from "react";
import { GetTotalsReturn } from "~features/checkout/legacy/helpers/getTotals";
import { LineItem, Order } from "~graphql/sdk";
import { ExtendedFile } from "~graphql/typed-document-nodes";
import {
  ServiceCost,
  useServiceCosts,
  UseServiceCostsProps,
} from "../hooks/useServiceCosts";
import { formatEventDateRange } from "~lib";
import { PickTypes } from "~lib/helpers/types";
import {
  getOrderSummaryLineItemSubtitle,
  getOrderSummaryLineItemTitle,
  GetOrderSummaryLineItemSubtitleParams,
  GetOrderSummaryLineItemTitleParams,
} from "../helpers";
import { UseNonSeatedMultibuyResult, useOrganization } from "~hooks";
import { useSinglePageCheckoutContext } from "~features/checkout/common/hooks/singlePageCheckoutContext";

type PartialOrder = PickTypes<
  Order,
  {
    event: {
      startDate: true;
      endDate: true;
      title: true;
      logo: true;
      venue: {
        timezone: true;
        locale: true;
      };
    };
    membership: {
      startDate: true;
      endDate: true;
      name: true;
      thumbnail: true;
      venue: {
        timezone: true;
        locale: true;
      };
    };
    lineItems: {
      edges: {
        node: PickTypes<
          LineItem,
          {
            id: true;
            name: true;
            quantity: true;
            type: true;
            price: true;
            originalPrice: true;
            seatZone: true;
            seatSection: true;
            seatLabel: true;
            ticketType: {
              name: true;
              id: true;
            };
            membershipType: {
              id: true;
            };
            pricingTierName: true;
          }
        > &
          GetOrderSummaryLineItemSubtitleParams &
          GetOrderSummaryLineItemTitleParams;
      }[];
    };
  }
>;
export interface OrderSummaryContext {
  title: string;
  subtitle?: string;
  image?: ExtendedFile;
  isLoading: boolean;
  isSubmitting: boolean;
  totalServiceCost: number;
  allowPromoCode?: boolean;
  isPromoCodeApplied?: boolean;
  serviceCosts: ServiceCost[];
  lineItems: (PartialOrder["lineItems"]["edges"][number]["node"] & {
    subtitle: string;
    title: string;
    multiBuy?: UseNonSeatedMultibuyResult["activePromotions"][number];
  })[];
  totals: GetTotalsReturn;
  timezone: string | undefined;
  locale: string | undefined;
  paymentPlanMetadata?: {
    depositAmount?: number;
    installmentAmount?: number;
    installmentCount?: number;
  };
}

const OrderSummaryContext = createContext<OrderSummaryContext | undefined>(
  undefined
);

export const useOrderSummaryContext = () => {
  const ctx = useContext(OrderSummaryContext);
  if (!ctx)
    throw new Error(
      "OrderSummaryContext must be used within OrderSummaryProvider"
    );
  return ctx;
};

export interface OrderSummaryProviderProps {
  order: PartialOrder & UseServiceCostsProps["order"];
  totals: GetTotalsReturn;
  isLoading: boolean;
  isSubmitting: boolean;
  allowPromoCode?: boolean;
  isPromoCodeApplied?: boolean;
  children: React.ReactNode;
  paymentPlanMetadata?: {
    depositAmount?: number;
    installmentAmount?: number;
    installmentCount?: number;
  };
  multiBuyPromotions?: UseNonSeatedMultibuyResult["activePromotions"];
}
export function OrderSummaryProvider({
  children,
  order,
  totals,
  isLoading,
  isSubmitting,
  allowPromoCode = false,
  isPromoCodeApplied = false,
  paymentPlanMetadata,
  multiBuyPromotions = [],
}: OrderSummaryProviderProps) {
  const { organization } = useOrganization();

  const serviceCosts = useServiceCosts({
    order,
    totals,
  });

  const totalServiceCost = serviceCosts.reduce(
    (acc, { price }) => +(acc + (price ?? 0)).toFixed(2),
    0
  );

  const startDate = order?.event?.startDate ?? order?.membership?.startDate;
  const endDate = order?.event?.endDate ?? order?.membership?.endDate;
  const timezone =
    order?.event?.venue?.timezone ?? order?.membership?.venue?.timezone;
  const locale =
    order?.event?.venue?.locale ?? order?.membership?.venue?.locale;

  const subtitle = formatEventDateRange(startDate, endDate, {
    format: "basicLong",
    timeZone: timezone,
    locale,
  });

  const lineItems = order?.lineItems?.edges.map((edge) => {
    const item = edge?.node;

    const title = getOrderSummaryLineItemTitle(item);

    const subtitle = getOrderSummaryLineItemSubtitle(
      item,
      organization.point?.name
    );

    const multiBuy = multiBuyPromotions.find(
      (promo) =>
        promo.promotion?.id === item.id ||
        (item?.ticketType?.id || item?.membershipType?.id) ===
          promo?.getType?.id
    );

    return {
      ...item,
      subtitle,
      title,
      multiBuy,
    };
  });

  return (
    <OrderSummaryContext.Provider
      value={{
        image: order?.event?.logo ?? order?.membership?.thumbnail,
        title: order?.event?.title ?? order?.membership?.name,
        subtitle,
        allowPromoCode,
        isPromoCodeApplied,
        totalServiceCost,
        serviceCosts,
        lineItems: lineItems || [],
        totals,
        isLoading,
        isSubmitting,
        timezone,
        locale,
        paymentPlanMetadata,
      }}
    >
      {children}
    </OrderSummaryContext.Provider>
  );
}
